import type React from "react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { DrawioContainer } from "~/components/Document/drawioView";
import { TOAST_MESSAGES } from "~/constants/messages";
import type { RootState } from "~/store";
import styles from "./index.module.scss";

interface Props {
  formData: {
    id: string;
    content: string;
  };
}

const HistorySystemArchitecture: React.FC<Props> = ({ formData }) => {
  const [currentXml, setCurrentXml] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isLegacyFormat, setIsLegacyFormat] = useState(false);
  const { currentUser, currentProject } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentProject) {
      // プロジェクトが選択されていない場合はホームページにリダイレクト
      navigate(`/${currentUser.organization_id}/home`);
      return;
    }

    if (!formData?.content) {
      toast.error(TOAST_MESSAGES.HISTORY.SYSTEM_ARCHITECTURE_NOT_EXIST);
      navigate(`/${currentUser.organization_id}/project/${currentProject.id}/dashboard`);
      return;
    }

    try {
      const parsedData = JSON.parse(formData.content);

      // 旧バージョンのデータ形式（elements/mermaid）をチェック
      if (parsedData.elements || parsedData.mermaid) {
        setIsLegacyFormat(true);
        setIsLoading(false);
        return;
      }

      if (parsedData.xml) {
        setCurrentXml(parsedData.xml);
      }
    } catch (_error) {
      toast.error(TOAST_MESSAGES.HISTORY.SYSTEM_ARCHITECTURE_LOAD_FAILED);
      setIsLegacyFormat(true);
    } finally {
      setIsLoading(false);
    }
  }, [formData, currentUser, currentProject, navigate]);

  if (isLoading) {
    return <div>ロード中...</div>;
  }

  return (
    <div className={styles["historyContainer"]}>
      {isLegacyFormat ? (
        <p className={styles["errorMessage"]}>
          このデータは旧形式で作成されており、表示できません。
        </p>
      ) : (
        <DrawioContainer
          documentType="system-architecture"
          documentName="システム構成図"
          xml={currentXml || ""}
        />
      )}
    </div>
  );
};

export default HistorySystemArchitecture;
